/**
 * The unfinished setup, kept on the phone — a quick round or a competition.
 *
 * Setting a round up means leaving the app — WhatsApp to see who's in, the
 * club's site for the tee — and coming back is an unmount at best and a cold
 * start at worst. Everything typed was lost. Same rules as the course wizard's
 * draft (`components/course/courseDraft.js`): keyed by SOCIETY, time-stamped,
 * offered back openly with a way to start fresh, and cleared when the round or
 * competition is created. Cleared on sign-out too (AuthContext
 * `clearUserState`, by prefix — both kinds share it).
 *
 * Two kinds, kept apart: a quick round lives for a DAY (it is played today or
 * not at all), a competition for a WEEK (an organiser sets one up over several
 * evenings, between replies from the field). Opening one never offers the
 * other's draft.
 */
export const DRAFT_PREFIX = 'quick_round_draft_';
const TTL_MS = {
  quick: 24 * 60 * 60 * 1000,
  full: 7 * 24 * 60 * 60 * 1000,
};

// The quick key is unchanged from before the competition draft existed, so a
// draft saved by the previous build is still found.
const keyFor = (societyId, kind) => (kind === 'full'
  ? `${DRAFT_PREFIX}${societyId}_full`
  : `${DRAFT_PREFIX}${societyId}`);

export const readQuickDraft = (societyId, kind = 'quick') => {
  if (!societyId) return null;
  try {
    const raw = localStorage.getItem(keyFor(societyId, kind));
    if (!raw) return null;
    const draft = JSON.parse(raw);
    if (!draft?.data || Date.now() - (draft.savedAt || 0) > (TTL_MS[kind] || TTL_MS.quick)) {
      localStorage.removeItem(keyFor(societyId, kind));
      return null;
    }
    return draft;
  } catch {
    return null;
  }
};

/** `extra` carries what a competition needs beyond the form itself (which
 *  event type it is, the Order of Merit links) and is spread onto the record. */
export const writeQuickDraft = (societyId, data, step, kind = 'quick', extra = {}) => {
  if (!societyId) return;
  try {
    localStorage.setItem(keyFor(societyId, kind),
      JSON.stringify({ ...extra, data, step, savedAt: Date.now() }));
  } catch { /* storage full or blocked: the draft is a convenience */ }
};

export const clearQuickDraft = (societyId, kind = 'quick') => {
  if (!societyId) return;
  try { localStorage.removeItem(keyFor(societyId, kind)); } catch { /* nothing to clear */ }
};
